import { forwardRef, useId } from 'react';
import type { InputHTMLAttributes, ReactNode } from 'react';
import { brandColors } from '../../theme';

export interface InputProps extends Omit<InputHTMLAttributes<HTMLInputElement>, 'prefix'> {
  label?: string;
  error?: string;
  helperText?: string;
  fullWidth?: boolean;
  prefix?: ReactNode;
  suffix?: ReactNode;
  selectAllOnFocus?: boolean;
}

export const Input = forwardRef<HTMLInputElement, InputProps>(
  (
    {
      label,
      error,
      helperText,
      fullWidth = false,
      className = '',
      id,
      style,
      prefix,
      suffix,
      selectAllOnFocus = false,
      onFocus,
      ...props
    },
    ref
  ) => {
    const generatedId = useId();
    const inputId = id || generatedId;

    const hasAddon = prefix || suffix;

    const handleFocus = (e: React.FocusEvent<HTMLInputElement>): void => {
      onFocus?.(e);
      if (selectAllOnFocus) {
        e.target.select();
      }
    };

    // Different base styles depending on whether we have addons
    const baseStyles = hasAddon
      ? 'px-3 py-2 transition-all border-none focus:outline-none disabled:opacity-50 disabled:cursor-not-allowed flex-1'
      : 'px-3 py-2 !border-2 rounded-md transition-all focus:outline-2 focus:outline-primary focus:outline-offset-0 disabled:opacity-50 disabled:cursor-not-allowed';

    const widthStyle = fullWidth ? 'w-full' : '';

    // Determine classes for state-based styling
    const inputStateClasses = error
      ? hasAddon
        ? ''
        : 'border-red-500 bg-red-50'
      : hasAddon
        ? ''
        : 'border-gray-300';

    const combinedClassName = [
      baseStyles,
      !hasAddon && widthStyle,
      inputStateClasses,
      className,
    ]
      .filter(Boolean)
      .join(' ');

    // Use brand colors for error border when needed
    const inputStyle: React.CSSProperties | undefined = error && !hasAddon
      ? {
          borderColor: brandColors.error,
          ...style,
        }
      : style;

    // Addon container styling
    const addonContainerClasses = error
      ? '!border-2 border-red-500 bg-red-50'
      : '!border-2 border-gray-300';

    const addonContainerStyle: React.CSSProperties = error
      ? {
          borderColor: brandColors.error,
        }
      : {};

    const inputElement = (
      <input
        ref={ref}
        id={inputId}
        className={combinedClassName}
        style={inputStyle}
        aria-invalid={error ? 'true' : 'false'}
        aria-describedby={
          error ? `${inputId}-error` : helperText ? `${inputId}-helper` : undefined
        }
        onFocus={handleFocus}
        {...props}
      />
    );

    return (
      <div className={fullWidth ? 'w-full' : ''}>
        {label && (
          <label
            htmlFor={inputId}
            className="block text-sm font-medium text-gray-700 mb-1"
          >
            {label}
          </label>
        )}
        {hasAddon ? (
          <div
            className={`flex items-stretch border-2 rounded-md overflow-hidden ${widthStyle} ${addonContainerClasses}`}
            style={addonContainerStyle}
          >
            {prefix && (
              <span className="flex items-center px-3 bg-gray-50 text-gray-700 border-r border-gray-300">
                {prefix}
              </span>
            )}
            {inputElement}
            {suffix && (
              <span className="flex items-center px-3 bg-gray-50 text-gray-700 border-l border-gray-300">
                {suffix}
              </span>
            )}
          </div>
        ) : (
          inputElement
        )}
        {error && (
          <p
            id={`${inputId}-error`}
            className="mt-1 text-sm font-medium flex items-start gap-1"
            style={{ color: brandColors.error }}
            role="alert"
          >
            <span className="flex-shrink-0 mt-0.5" aria-hidden="true">⚠</span>
            <span>{error}</span>
          </p>
        )}
        {helperText && !error && (
          <p
            id={`${inputId}-helper`}
            className="mt-1 text-sm text-gray-500"
          >
            {helperText}
          </p>
        )}
      </div>
    );
  }
);

Input.displayName = 'Input';
